import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Clapperboard, Loader2, RefreshCw, Search, SmilePlus, Zap } from 'lucide-react';
import { batchInsertReactionOverlays, insertReactionOverlay } from '../utils/reaction-overlay-insertion';

type ReactionTemplateId = 'corner_sticker' | 'bottom_pop' | 'center_punch';
type ReactionTiming = 'scene_start' | 'scene_middle' | 'scene_end';
type ReactionEmotionTag = 'all' | 'funny' | 'awkward' | 'warning' | 'shock' | 'angry' | 'point' | 'cute';

type ReactionAsset = {
  id: string;
  name: string;
  mediaType: 'image' | 'gif' | 'mp4';
  emotionTag: Exclude<ReactionEmotionTag, 'all'>;
  sourceUrl: string;
  previewUrl?: string;
  recommendedDurationMs?: number;
  safeTemplates?: ReactionTemplateId[];
  intensity?: 'low' | 'medium' | 'high';
  isHeavy?: boolean;
};

type ReactionTemplate = {
  id: ReactionTemplateId;
  name: string;
  description: string;
};

export const ReactionPanel = memo(function ReactionPanel() {
  const [searchQuery, setSearchQuery] = useState('');
  const [assets, setAssets] = useState<ReactionAsset[]>([]);
  const [templates, setTemplates] = useState<ReactionTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEmotion, setSelectedEmotion] = useState<ReactionEmotionTag>('all');
  const [selectedTemplateId, setSelectedTemplateId] = useState<ReactionTemplateId>('corner_sticker');
  const [selectedTiming, setSelectedTiming] = useState<ReactionTiming>('scene_middle');
  const videoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'REACTION_ASSETS_LIST') return;

      const success = event.data?.payload?.success ?? event.data?.data?.success;
      const nextAssets = event.data?.payload?.assets || event.data?.data?.assets || [];
      const nextTemplates = event.data?.payload?.templates || event.data?.data?.templates || [];
      setIsLoading(false);

      if (!success) return;
      setAssets(nextAssets);
      setTemplates(nextTemplates);
      if (!nextTemplates.some((template: ReactionTemplate) => template.id === selectedTemplateId) && nextTemplates[0]?.id) {
        setSelectedTemplateId(nextTemplates[0].id);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [selectedTemplateId]);

  const requestAssets = () => {
    setIsLoading(true);
    window.parent.postMessage({ type: 'REQUEST_REACTION_ASSETS' }, '*');
  };

  useEffect(() => {
    requestAssets();
  }, []);

  const filteredAssets = useMemo(() => {
    const loweredQuery = searchQuery.trim().toLowerCase();
    return assets.filter((asset) => {
      const matchesEmotion = selectedEmotion === 'all' || asset.emotionTag === selectedEmotion;
      const matchesTemplate = !asset.safeTemplates?.length || asset.safeTemplates.includes(selectedTemplateId);
      const matchesQuery =
        !loweredQuery ||
        asset.name.toLowerCase().includes(loweredQuery) ||
        asset.emotionTag.toLowerCase().includes(loweredQuery) ||
        asset.mediaType.toLowerCase().includes(loweredQuery);
      return matchesEmotion && matchesTemplate && matchesQuery;
    });
  }, [assets, searchQuery, selectedEmotion, selectedTemplateId]);

  const selectedTemplate = templates.find((template) => template.id === selectedTemplateId);

  const setVideoRef = (assetId: string, element: HTMLVideoElement | null) => {
    if (element) {
      videoRefs.current.set(assetId, element);
    } else {
      videoRefs.current.delete(assetId);
    }
  };

  const handleVideoHover = (assetId: string, shouldPlay: boolean) => {
    const video = videoRefs.current.get(assetId);
    if (!video) return;
    if (shouldPlay) {
      void video.play().catch(() => {});
    } else {
      video.pause();
      video.currentTime = 0;
    }
  };

  return (
    <div className="h-full overflow-y-auto p-3">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => batchInsertReactionOverlays(assets, 'light')}
            className="flex items-center justify-center gap-2 rounded-xl bg-[linear-gradient(135deg,#f59e0b_0%,#ea580c_100%)] px-3 py-3 text-xs font-black text-white shadow-sm transition-transform hover:scale-[1.02]"
          >
            <SmilePlus className="h-4 w-4" />
            가볍게 배치
          </button>
          <button
            type="button"
            onClick={() => batchInsertReactionOverlays(assets, 'impact')}
            className="flex items-center justify-center gap-2 rounded-xl bg-[linear-gradient(135deg,#8b5cf6_0%,#6d28d9_100%)] px-3 py-3 text-xs font-black text-white shadow-sm transition-transform hover:scale-[1.02]"
          >
            <Zap className="h-4 w-4" />
            임팩트 배치
          </button>
        </div>

        <div className="rounded-xl border border-border bg-background/50 p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search Meme..."
              className="h-10 w-full rounded-lg border border-border bg-background pl-9 pr-3 text-sm outline-none"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <select
            value={selectedTemplateId}
            onChange={(event) => setSelectedTemplateId(event.target.value as ReactionTemplateId)}
            className="h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none"
          >
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
          <select
            value={selectedTiming}
            onChange={(event) => setSelectedTiming(event.target.value as ReactionTiming)}
            className="h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none"
          >
            <option value="scene_start">씬 시작</option>
            <option value="scene_middle">씬 중간</option>
            <option value="scene_end">씬 끝</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <select
            value={selectedEmotion}
            onChange={(event) => setSelectedEmotion(event.target.value as ReactionEmotionTag)}
            className="h-10 rounded-lg border border-border bg-background px-3 text-sm outline-none"
          >
            <option value="all">전체</option>
            <option value="funny">웃김</option>
            <option value="awkward">당황</option>
            <option value="warning">경고</option>
            <option value="shock">충격</option>
            <option value="angry">분노</option>
            <option value="point">포인트</option>
            <option value="cute">귀여움</option>
          </select>
          <button
            type="button"
            onClick={requestAssets}
            className="flex h-10 items-center justify-center gap-2 rounded-lg border border-border bg-background text-sm font-medium transition-colors hover:bg-secondary"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            새로고침
          </button>
        </div>

        {selectedTemplate && (
          <div className="rounded-xl border border-border bg-secondary/20 px-3 py-2 text-[11px] text-muted-foreground">
            {selectedTemplate.description}
          </div>
        )}

        <div className="space-y-2">
          {isLoading && assets.length === 0 ? (
            <div className="flex h-56 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
              <div className="text-xs font-medium">짤 자산을 불러오는 중...</div>
            </div>
          ) : filteredAssets.length === 0 ? (
            <div className="flex h-56 flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border text-muted-foreground">
              <Clapperboard className="h-6 w-6 opacity-50" />
              <div className="text-xs font-medium">조건에 맞는 짤이 없습니다.</div>
            </div>
          ) : (
            filteredAssets.map((asset) => (
              <button
                key={asset.id}
                type="button"
                onClick={() => insertReactionOverlay({
                  asset,
                  templateId: selectedTemplateId,
                  timing: selectedTiming,
                })}
                onMouseEnter={() => handleVideoHover(asset.id, true)}
                onMouseLeave={() => handleVideoHover(asset.id, false)}
                className="flex w-full items-center gap-3 rounded-xl border border-border bg-background p-2 text-left transition-colors hover:bg-secondary"
              >
                <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg border border-border bg-black/50">
                  {asset.mediaType === 'mp4' ? (
                    <video
                      ref={(element) => setVideoRef(asset.id, element)}
                      src={asset.previewUrl || asset.sourceUrl}
                      muted
                      loop
                      playsInline
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <img
                      src={asset.previewUrl || asset.sourceUrl}
                      alt={asset.name}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold">{asset.name}</div>
                  <div className="mt-1 flex items-center gap-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                    <span>{asset.mediaType}</span>
                    <span>•</span>
                    <span>{asset.emotionTag}</span>
                    {asset.isHeavy && (
                      <>
                        <span>•</span>
                        <span>heavy</span>
                      </>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
});
